import { AxiosRequestConfig } from "axios";
import React, { useEffect } from "react";
import { useStore } from "../../../Store";
import PageLayout from "../../Controls/PageLayout";
import Helpers from "../../Functions/Helpers";
import {
  Button,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { Result } from "../../Result/Result";
import { UnAuthorizedIcon } from "../../Result/UnAuthorized";
import { ErrorResult, PostErrorLog } from "../../Functions/ErrorLogApi";
import { useMessageParentOrRedirect } from "../../../utils/useMessageParentOrRedirect";
import { redirectParentPage } from "../../../utils/redirectParentPage";

export default function DhAuth({
  errorResultProp,
}: {
  errorResultProp?: ErrorResult;
}) {
  const {
    token,
    selectedConsent,
    setSelectedConsent,
    sessionDetails,
    selectedDataHolder,
    setSelectedDataHolder,
    data,
    setData,
    isEmbedded,
    setLoading,
  } = useStore();

  const { messageParentOrRedirect } = useMessageParentOrRedirect();

  const [errorResult, setErrorResult] = React.useState<ErrorResult | null>(
    errorResultProp ?? null
  );

  useEffect(() => {
    setLoading(false);

    function PutConsent() {
      let configPut: AxiosRequestConfig = {
        method: "put",
        url:
          process.env.REACT_APP_PUBLIC_API_BASE_URI +
          "/consent/" +
          sessionDetails?.consentId,
        headers: {
          Authorization: "Bearer " + token?.access_token,
        },
        data: selectedConsent,
      };

      Helpers.CallApi(
        configPut,
        () => {
          // handle success
          GetDataHolderAuthRequest();
        },
        (message) => {
          // handle error
          HandleError(message);
        }
      );
    }

    function GetDataHolderAuthRequest() {
      let configGet: AxiosRequestConfig = {
        method: "get",
        url:
          process.env.REACT_APP_PUBLIC_API_BASE_URI +
          "/consent/" +
          sessionDetails?.consentId +
          "/dataHolderAuthRequest",
        headers: {
          Authorization: "Bearer " + token?.access_token,
        },
      };

      Helpers.CallApi(
        configGet,
        (response) => {
          // handle redirecting to data holder
          if (process.env.REACT_APP_REDIRECT_SELECT_CUSOMER_URL) {
            redirectParentPage(
              process.env.REACT_APP_REDIRECT_SELECT_CUSOMER_URL
            );
          } else {
            redirectParentPage(response?.data?.redirectUri);
          }
        },
        (message) => {
          // handle error
          HandleError(message);
        }
      );
    }

    // This function MUST only be called when we are use the mock bank flow.
    function GetData() {
      let configGet: AxiosRequestConfig = {
        method: "get",
        url:
          process.env.REACT_APP_PUBLIC_API_BASE_URI +
          "/portal/consent/" +
          sessionDetails?.consentId,
        headers: {
          Authorization: "Bearer " + token?.access_token,
        },
      };

      Helpers.CallApi(
        configGet,
        (response) => {
          // handle success
          const responseData = response.data.data;
          const dataHolderBrandId = responseData.consent.dataHolderBrandId;
          setData(responseData);
          setSelectedConsent(responseData);
          setSelectedDataHolder({
            dataHolderBrandId: dataHolderBrandId,
          });
          GetDataHolderAuthRequest();
        },
        (message) => {
          // handle error
          HandleError(message);
        }
      );
    }

    // If we are going directly to the bank (direct-to-bank or amend flow), we need to get the session data from the API as this will be the first
    // screen. In the standard flow, we get the session data in an earlier screen. We also don't need to PutConsent
    // in the direct-to-bank/amend flow, as the consent has already been set up to go direct to the bank.
    if (sessionDetails!.flow === "direct-to-bank" || sessionDetails!.flow === "amend") {
      GetData();
    } else {
      PutConsent();
    }
  }, []);

  function HandleError(message: string) {
    const errorData = {
      bankName: selectedDataHolder?.brand?.name ?? "a bank",
      sessionId: sessionDetails?.sessionId ?? "unknown",
      consentId: data?.consent?.id ?? "unknown",
      consentStartTime: data?.consent?.startTime ?? "unknown",
      timeUtcString:
        errorResult?.time?.toUTCString() ?? new Date().toUTCString(),
      currentUri: window.location.href,
    };

    PostErrorLog(errorData.sessionId, {
      consentId: errorData.consentId,
      userId: errorData.sessionId,
      bankName: errorData.bankName,
      errorTime: errorData.timeUtcString,
      consentStartTime: errorData.consentStartTime,
      currentUri: errorData.currentUri,
      message: `An error occured when redirect a user to a DataHolder: ${message}`,
    });

    setErrorResult({
      message,
      time: new Date(),
      data: errorData,
    });
  }

  function GetEmailLink() {
    const subject = `SessionID: ${errorResult?.data?.sessionId} - Redirect to ${errorResult?.data?.bankName} failed`;
    const body = `Redirect to ${errorResult?.data?.bankName} failed at ${errorResult?.data?.timeUtcString}
SessionID: ${errorResult?.data?.sessionId}
ConsentID: ${errorResult?.data?.consentId}
ConsentStartTime: ${errorResult?.data?.consentStartTime}
CurrentURI: ${errorResult?.data?.currentUri}
Error: ${errorResult?.message}
    `;

    return `mailto:help@acsiss.com.au?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }

  function clickOkError() {
    messageParentOrRedirect("ERROR", {
      message: errorResult?.message ?? "Unknown Error",
      isEmbedded: isEmbedded,
      flow: sessionDetails?.flow!,
      consentId: data!.consent?.id,
      state: data!.session.state,
      redirectUriBase: data?.session.redirectUri!,
      sessionId: sessionDetails?.sessionId,
    });
  }

  if (errorResult) {
    return (
      <PageLayout>
        <Result
          icon={<UnAuthorizedIcon />}
          title="Failed to redirect"
          subtitle={"Sorry we couldn't redirect you to your bank."}
          extra={
            <>
              <VStack align="left" fontSize={14} py={8} gap={8}>
                <Text>
                  <strong>There's a few reasons why this might happen:</strong>
                  <UnorderedList>
                    <ListItem>A network error may have occurred.</ListItem>
                    <ListItem>
                      We might have encountered an error communicating with your
                      bank.
                    </ListItem>
                  </UnorderedList>
                </Text>

                <Text>
                  <strong>What to do now?</strong>
                  <UnorderedList>
                    <ListItem>
                      Try again. Sometimes things go wrong, and it might work
                      the next time.
                    </ListItem>
                    <ListItem>
                      Click the 'Send Error Report' button below to send us an
                      email with the error details.
                    </ListItem>
                  </UnorderedList>
                </Text>
              </VStack>

              <HStack>
                <Button
                  onClick={() => {
                    redirectParentPage(GetEmailLink());
                  }}
                >
                  Send Error Report
                </Button>
                <Button colorScheme="blue" onClick={clickOkError} width={24}>
                  OK
                </Button>
              </HStack>
            </>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <VStack p={20} gap={8}>
        <Spinner size="xl" />
        <Text>One moment, we're sending you to your bank.</Text>
      </VStack>
    </PageLayout>
  );
}
